import { Request } from '../request';
import { Setting } from 'types/api';
import { EnumFields } from 'types/enumerationData';
import type {
    EWebinarScheduleType,
    ServerMeta,
    TMedication,
    TInsuranceForecast,
    TGrowthTierSetting,
} from 'types/common';
import type { TMedicationSetting } from 'types/medicationSetting';
import { EGreetingMessageTony } from 'types/ai';
import { IProviderSignature } from 'types/treatment';

export const uploadFile = (data: FormData) =>
    Request.post<FormData>({
        url: '/oms-server/uploadFile',
        params: data,
        config: {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        },
    });

export const uploadPrivateFileByProspect = (data: FormData) =>
    Request.post<FormData>({
        url: '/oms-server/uploadPrivateFileByProspect',
        params: data,
        config: {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        },
    });

export const uploadPrivateFileByProviderId = (data: FormData) =>
    Request.post<FormData>({
        url: '/oms-server/uploadPrivateFileByProviderId',
        params: data,
        config: {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        },
    });

export const uploadFilePrivate = (data: FormData) =>
    Request.post<FormData>({
        url: '/oms-server/uploadPrivateFileByProviderId',
        params: data,
        config: {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        },
    });

export const uploadFileByType = (data: FormData) =>
    Request.post<FormData>({
        url: '/oms-server/uploadFileByType',
        params: data,
        config: {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        },
    });

export const getSettings = () =>
    Request.get<Setting[]>({
        url: '/admin-server/setting/list',
        disableErrorNote: true,
    });

export const getCalendarColors = () =>
    Request.get<Setting[]>({
        url: '/schedule-server/openapi/admin/listSettingByType?type=color',
        disableErrorNote: true,
    });

export const getAdminCalendarColors = () =>
    Request.get<Setting[]>({
        url: '/admin-server/setting/listByType?type=color',
        disableErrorNote: true,
    });

export const getListSetting = () =>
    Request.get({
        url: '/schedule-server/openapi/admin/listSetting',
        disableErrorNote: true,
    });

export const getAdminListSetting = () =>
    Request.get({
        url: '/admin-server/setting/list',
        disableErrorNote: true,
    });

export const getListSettingByType = (type: EnumFields) =>
    Request.get({
        url: '/schedule-server/openapi/admin/listSettingByType',
        params: { type },
        disableErrorNote: true,
    });

export const getConfigFeaturedItems = () =>
    Request.get<ServerMeta[]>({
        url: '/schedule-server/configFeaturedItems/getAll',
        disableErrorNote: true,
    });

export const getKlarityServiceSpecialtyCondition = () =>
    Request.get<Record<string, string[]>>({
        url: '/schedule-server/klarityServiceSpecialty/condition/mapping',
    });

export const uploadProviderFile = (data: FormData) =>
    Request.post<FormData>({
        url: '/oms-server/uploadPrivateFile',
        params: data,
        config: {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        },
    });

export const getKlarityProgramList = () => Request.get({ url: '/schedule-server/provider/program/all/list' });

export const getKlarityProgramDetail = (id: number) =>
    Request.get({ url: '/schedule-server/klarity/program/getByIdInfo', params: { id } });

export type TEnrollKlarityProgram = {
    programId: number;
    states: string;
    contractSignName: string;
    contractSignDate: string;
    contractSignAddress: string;
};

export const enrollKlarityProgram = (data: TEnrollKlarityProgram) =>
    Request.post({ url: '/schedule-server/provider/program/enroll', params: data });

export const updateKlarityProgramState = (programId: number, states: string) =>
    Request.post({ url: '/schedule-server/provider/program/state/update', params: { programId, states } });

export const disContinueProgram = (pid: number) =>
    Request.put({ url: `/schedule-server/provider/program/disContinue/${pid}` });

export const getProviderMedicationList = () => Request.get({ url: '/schedule-server/provider/medication/list' });

export const updateProviderMedicationList = (data: TMedication[]) =>
    Request.post({ url: '/schedule-server/provider/medication/saveOrUpdate/batch', params: data });

export const calendlyProviderSave = (scheduleType: EWebinarScheduleType) =>
    Request.post({ url: '/schedule-server/calendly/provider/save', params: { scheduleType } });

export const getAllInsuranceList = () => Request.get({ url: '/schedule-server/provider/insurance/listByState' });

export const getInsuranceListByOps = () => Request.get({ url: '/admin-server/provider/insurance/listByState' });

export const getPPAId = () => Request.get({ url: '/schedule-server/channel/ppa/id' });

export const getPPAIdByOps = () => Request.get({ url: '/admin-server/channel/ppa/id' });

export const getMedicationSetting = () =>
    Request.get({ url: '/schedule-server/provider/uniprofile/medication/non/prescribable/list' });

export const saveMedicationSetting = (data: TMedicationSetting[]) =>
    Request.post({ url: '/schedule-server/provider/uniprofile/medication/save', params: data });

export const joinWaitlist = () =>
    Request.post({
        url: '/schedule-server/common/provider/select/record/save',
        params: {
            recordType: 'GROWTH_TIER_WAIT_LIST_STATUS',
            value: '1',
        },
    });

export const growthSettingSave = (val: TGrowthTierSetting) =>
    Request.post({
        url: `/schedule-server/klarity/provider/setting/growth/tier/${val}/save`,
    });

type TJoinPrescription = {
    type: 'KLARITY_E_PRESCRIPTIONS';
    key: 'MONTHLY_STRIPE_ID';
    paymentMethodId: string; // 'pm_xxxxxxxxxxxxxxxxxx',
    couponId?: string; // 'coupon_id_optional',
    email: string; //'user@example.com',
    country: string; // 'US',
    postalCode: string; // '12345'
};

export const joinPrescription = (data: TJoinPrescription) =>
    Request.post({
        url: '/schedule-server/provider/channel/stripe/unifiedPay',
        params: data,
    });

export const getKanmonInfo = () => Request.get({ url: '/schedule-server/provider/financials/kanmon/info' });

export const getKanmonToken = () => Request.post({ url: '/schedule-server/provider/financials/kanmon/generateToken' });

export const getInsuranceForecastList = () =>
    Request.get({ url: '/schedule-server/provider/financials/insurance/forecast/list?current=1&size=10' });

export const saveInsuranceForecastList = (data: TInsuranceForecast) =>
    Request.post({
        url: '/schedule-server/provider/financials/insurance/forecast/save',
        params: data,
    });

export const calculateInsuranceForecast = (data: TInsuranceForecast) =>
    Request.post({
        url: '/schedule-server/provider/financials/insurance/forecast/calculate',
        params: data,
        disableErrorNote: true,
    });

export const saveGreetingMessage = (params: {
    messageTone: string;
    isAiAssisted: boolean;
    status: 'A' | 'D';
    customPrompt?: string;
    greetingMessage?: string;
}) => {
    return Request.post({
        url: '/schedule-server/provider/greeting/message/save',
        params,
    });
};

export const getProspectSettingInfo = () => {
    return Request.get({
        url: '/schedule-server/provider/prospect/setting/info',
    });
};

export const getGreetingMessageExample = (messageTone: EGreetingMessageTony, customPrompt: string) => {
    return Request.post({
        url: '/schedule-server/provider/greeting/message/example',
        params: {
            messageTone,
            customPrompt,
        },
    });
};

export const getContractLatestList = () => Request.get({ url: '/schedule-server/common/contract/latest/list' });

export const uploadProviderPrivateFile = (data: FormData) =>
    Request.post<FormData>({
        url: '/schedule-server/common/private/file/upload/provider',
        params: data,
        config: {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        },
    });

export const getProviderPrivateFileSigned = (resourceKey: string) =>
    Request.post({
        url: '/schedule-server/common/private/file/signed',
        params: { resourceKey },
    });

type TGetProviderPrescriptionMedicationList = {
    businessType?: string;
    clientId?: number;
    current?: number;
    drugName?: string;
    orderKey?: string;
    orderType?: string;
    patientName?: string;
    providerId?: number;
    size?: number;
    sortBy?: string;
    sortType?: 'ASC' | 'DESC';
    status?: string[];
};

export const getProviderPrescriptionMedicationList = (param: TGetProviderPrescriptionMedicationList) =>
    Request.post({ url: '/schedule-server/provider/prescription/medication/list', params: param });

// overrideProviderId：group owner 替成员加 / 取签名时切到该成员（签名存在成员名下）；不传 = 登录者本人
export const createProviderSignature = (data: IProviderSignature, overrideProviderId?: number | string) =>
    Request.post({
        url: '/schedule-server/provider/signature/create',
        params: data,
        overrideProviderId,
    });

export const getDefaultProviderSignature = (overrideProviderId?: number | string) =>
    Request.get({
        url: '/schedule-server/provider/signature/default',
        overrideProviderId,
    });

export const getCurrentUser = () => Request.get({ url: `/schedule-server/api/auth/currentUser` });

export const exitPreviewProvider = () =>
    Request.post({ url: `/schedule-server/public/api/v1/switch-account/ops-exit` });

// 独立 NPI 校验通道（eligibility 文档 §3.5）：后端直连 NPPES，不校验 owner、不需要 spa_api_key，
// 任何登录 provider 都能调——给「还没开通 Insurance Billing」的场景用。
// 出参与 api/insuranceBilling 的 lookupNpi（走 AskChart 那条）完全一致（后端复用同一份模型），
// 所以两者对 NpiRegistryBox 可互换。
// 业务错误码：91041 = NPPES 查无此 NPI（→ notfound）、1009 = NPPES 不可达
export const lookupNpiStandalone = (npi: string) =>
    Request.get({ url: '/schedule-server/provider/npi-lookup', params: { npi }, disableErrorNote: true });
